import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import Overview from "./Overview";
import UsersManager from "./UsersManager";
import ClientsManager from "./ClientsManager";
import StatisticsPage from "./StatisticsPage";
import CompanySettings from "./CompanySettings";
import SectionsManager from "./SectionsManager";
import ReportsPage from "./ReportsPage";
import NotificationsPage from "./NotificationsPage";
import VendorsManager from "./VendorsManager";
import SupervisorsManager from "./SupervisorsManager";
import OperatorsManager from "./OperatorsManager";
import NotesInbox from "./NotesInbox";
import StockManager from "./StockManager";
import CompetitorMap from "./CompetitorMap";
import ApiAccessPage from "./ApiAccessPage";
import {
  LayoutDashboard, Users, UserCheck, Truck,
  MapPin, ClipboardList, FileText, BarChart2,
  Bell, Settings, Menu, X, MoreHorizontal, HardHat, MessageSquare, Package, Code,
} from "lucide-react";
import { useBranding } from "../../context/BrandingContext";

// ── Nav icon map ───────────────────────────────────────────────────────────
const NAV_ICONS = {
  overview:      LayoutDashboard,
  users:         Users,
  supervisors:   UserCheck,
  operators:     HardHat,
  vendors:       Truck,
  sections:      MapPin,
  clients:       ClipboardList,
  reports:       FileText,
  statistics:    BarChart2,
  notifications: Bell,
  notes:          MessageSquare,
  stock:          Package,
  competitor_map: MapPin,
  api_access:     Code,
  settings:       Settings,
};

// ── Base nav items (stock added dynamically when enabled) ─────────────────
const BASE_NAV_ITEMS = [
  { id: "overview",      labelKey: "nav_overview"      },
  { id: "sections",      labelKey: "nav_sections"       },
  { id: "users",         labelKey: "nav_users"          },
  { id: "supervisors",   labelKey: "nav_supervisors"   },
  { id: "operators",     labelKey: "nav_operators"     },
  { id: "vendors",       labelKey: "nav_vendors"        },
  { id: "clients",       labelKey: "nav_clients"        },
  { id: "reports",       labelKey: "nav_reports"        },
  { id: "statistics",    labelKey: "nav_statistics"     },
  { id: "notes",         labelKey: "nav_notes"          },
  { id: "notifications", labelKey: "nav_notifications"  },
  { id: "settings",      labelKey: "nav_settings"       },
];

// Mobile bottom tab bar — 5 most-used items
const MOBILE_TABS = ["overview", "clients", "notifications", "settings"];

function AdminDashboard() {
  const { branding } = useBranding();
  const { currentUser, companyId, logout } = useAuth();
  const { t } = useLanguage();
  const [activePage,      setActivePage]      = useState("overview");
  const [sidebarOpen,     setSidebarOpen]     = useState(false);
  const [moreMenuOpen,    setMoreMenuOpen]    = useState(false);
  const [mapFocusClient,  setMapFocusClient]  = useState(null);
  // Tracks which pages have been visited at least once — keeps them mounted (hidden) after first load
  const [mountedPages,    setMountedPages]    = useState(new Set(["overview"]));
  const [notesUnread,  setNotesUnread]  = useState(0);
  const [stockEnabled,    setStockEnabled]    = useState(false);
  const [companyFeatures, setCompanyFeatures] = useState({});
  const [companyName,     setCompanyName]     = useState("");
  const [companyLogo,     setCompanyLogo]     = useState("");

  // Load unread notes count on mount
  useEffect(() => {
    async function loadUnread() {
      try {
        const { db } = await import("../../firebase");
        const { collection, getDocs, query, where } = await import("firebase/firestore");
        const snap = await getDocs(query(
          collection(db, "companies", companyId, "notes"),
          where("read", "==", false)
        ));
        setNotesUnread(snap.size);
      } catch (err) { /* silent — badge is informational */ }
    }
    async function loadStockFlag() {
      try {
        const { db } = await import("../../firebase");
        const { doc, getDoc } = await import("firebase/firestore");
        const snap = await getDoc(doc(db, "companies", companyId));
        if (snap.exists()) {
          const data = snap.data();
          setStockEnabled(data.stock_feature_enabled === true);
          setCompanyFeatures(data);
          if (data.name)     setCompanyName(data.name);
          if (data.logo_url) setCompanyLogo(data.logo_url);
        }
      } catch (err) {
        console.error("Feature flags load error:", err.message);
      }
    }
    loadUnread();
    loadStockFlag();
  }, [companyId]);

  function navigate(id) {
    setActivePage(id);
    setSidebarOpen(false);
    setMoreMenuOpen(false);
    setMountedPages(prev => new Set([...prev, id]));
  }

  // Called from ClientsManager "Show on Map" button — navigates to Coverage Map focused on that client
  function handleShowOnMap(client) {
    setMapFocusClient(client);
    setActivePage("competitor_map");
    setMountedPages(prev => new Set([...prev, "competitor_map"]));
    setSidebarOpen(false);
    setMoreMenuOpen(false);
  }

  // Build nav: competitor_map goes right after overview (position 1), stock+api_access before settings
  const NAV_ITEMS = (() => {
    const items = [...BASE_NAV_ITEMS];
    // Coverage Map is always enabled for all companies
    items.splice(1, 0, { id: "competitor_map", labelKey: "nav_competitor_map" });
    // Append stock and api_access before settings
    const last   = items[items.length - 1];
    const middle = items.slice(0, -1);
    const extras = [];
    if (stockEnabled)                       extras.push({ id: "stock",      labelKey: "nav_stock"      });
    if (companyFeatures.api_access_enabled) extras.push({ id: "api_access", labelKey: "nav_api_access" });
    return extras.length > 0 ? [...middle, ...extras, last] : [...items];
  })();

  const activeItem = NAV_ITEMS.find(n => n.id === activePage);
  const ActiveIcon = NAV_ICONS[activePage];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">

      {/* ── Navbar ──────────────────────────────────────────────────────── */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 md:px-6 py-3 flex justify-between items-center w-full">

          {/* Left: hamburger + logo */}
          <div className="flex items-center gap-2">
            <button
              className="lg:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
              onClick={() => setSidebarOpen(o => !o)}
            >
              {sidebarOpen
                ? <X className="w-5 h-5" />
                : <Menu className="w-5 h-5" />}
            </button>
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.platform_name} className="w-8 h-8 object-contain rounded-lg" />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: branding.primary_color }}>
                <span className="text-white text-xs font-bold">{(branding.platform_name || "P").charAt(0).toUpperCase()}</span>
              </div>
            )}
            <span className="font-extrabold text-gray-900 tracking-wide">{(branding.platform_name || "Pinpoint").toUpperCase()}</span>
            <span className="hidden sm:inline text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
              {t("role_admin")}
            </span>
          </div>

          {/* Right */}
          <div className="flex items-center gap-3">
            {/* Mobile: show current page name */}
            <span className="lg:hidden text-sm text-gray-500 font-medium flex items-center gap-1">
              {ActiveIcon && <ActiveIcon className="w-4 h-4" />}
              {t(activeItem?.labelKey)}
            </span>
            {/* Desktop extras */}
            {(companyLogo || companyName) && (
              <div className="hidden lg:flex items-center gap-2">
                {companyLogo && (
                  <img src={companyLogo} alt="logo"
                    className="w-7 h-7 rounded-lg object-cover border border-gray-200 shrink-0" />
                )}
                {companyName && (
                  <span className="text-base font-bold text-gray-800 tracking-tight" style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
                    {companyName}
                  </span>
                )}
              </div>
            )}
            <span className="hidden md:inline text-sm text-gray-500 truncate max-w-[150px]">{currentUser?.email}</span>
            <button onClick={logout} className="text-xs md:text-sm text-red-500 hover:text-red-700 font-medium">
              {t("logout")}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Sidebar + Content ────────────────────────────────────────────── */}
      <div className="flex flex-1 w-full">

        {/* Backdrop overlay for tablet */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/30 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed top-[57px] left-0 z-30 h-[calc(100vh-57px)] w-56 bg-white shadow-xl overflow-y-auto
          transition-transform duration-200
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:sticky lg:top-[57px] lg:translate-x-0 lg:shadow-none lg:h-[calc(100vh-57px)] lg:w-52 lg:shrink-0 lg:overflow-y-auto lg:border-r lg:border-gray-200 lg:bg-white
        `}>
          <div className="p-3 lg:py-3 lg:px-2">
              {/* Mobile sidebar header */}
              <div className="lg:hidden border-b border-gray-100 pb-2 mb-2">
                <p className="text-xs text-gray-500 font-medium truncate">{currentUser?.email}</p>
                <p className="text-xs text-gray-300 truncate">{companyId}</p>
              </div>
              <nav className="space-y-0.5">
                {NAV_ITEMS.map(item => {
                  const Icon = NAV_ICONS[item.id];
                  const badge = item.id === "notes" && notesUnread > 0 ? notesUnread : null;
                  return (
                    <SidebarItem key={item.id}
                      Icon={Icon} label={t(item.labelKey)}
                      active={activePage === item.id}
                      badge={badge}
                      onClick={() => navigate(item.id)} />
                  );
                })}
              </nav>
          </div>
        </aside>

        {/* Page content */}
        <main className="flex-1 min-w-0 p-3 md:p-6 pb-24 lg:pb-6">
          {/* Lazy-mounted pages — mount once on first visit, then kept alive hidden */}
          {mountedPages.has("overview") && (
            <div style={{ display: activePage === "overview" ? undefined : "none" }}>
              <Overview companyId={companyId} />
            </div>
          )}
          {mountedPages.has("users") && (
            <div style={{ display: activePage === "users" ? undefined : "none" }}>
              <UsersManager companyId={companyId} />
            </div>
          )}
          {mountedPages.has("supervisors") && (
            <div style={{ display: activePage === "supervisors" ? undefined : "none" }}>
              <SupervisorsManager companyId={companyId} />
            </div>
          )}
          {mountedPages.has("operators") && (
            <div style={{ display: activePage === "operators" ? undefined : "none" }}>
              <OperatorsManager companyId={companyId} />
            </div>
          )}
          {mountedPages.has("vendors") && (
            <div style={{ display: activePage === "vendors" ? undefined : "none" }}>
              <VendorsManager companyId={companyId} />
            </div>
          )}
          {mountedPages.has("sections") && (
            <div style={{ display: activePage === "sections" ? undefined : "none" }}>
              <SectionsManager companyId={companyId} />
            </div>
          )}
          {mountedPages.has("reports") && (
            <div style={{ display: activePage === "reports" ? undefined : "none" }}>
              <ReportsPage companyId={companyId} />
            </div>
          )}
          {mountedPages.has("statistics") && (
            <div style={{ display: activePage === "statistics" ? undefined : "none" }}>
              <StatisticsPage companyId={companyId} />
            </div>
          )}
          {mountedPages.has("notes") && (
            <div style={{ display: activePage === "notes" ? undefined : "none" }}>
              <NotesInbox companyId={companyId} />
            </div>
          )}
          {mountedPages.has("notifications") && (
            <div style={{ display: activePage === "notifications" ? undefined : "none" }}>
              <NotificationsPage companyId={companyId} />
            </div>
          )}
          {mountedPages.has("competitor_map") && (
            <div style={{ display: activePage === "competitor_map" ? undefined : "none" }}>
              <CompetitorMap companyId={companyId} focusClient={mapFocusClient} onFocusDone={() => setMapFocusClient(null)} />
            </div>
          )}
          {mountedPages.has("api_access") && companyFeatures.api_access_enabled && (
            <div style={{ display: activePage === "api_access" ? undefined : "none" }}>
              <ApiAccessPage companyId={companyId} />
            </div>
          )}
          {/* Always remount — these pages manage their own sensitive/heavy state */}
          {activePage === "clients"  && <ClientsManager companyId={companyId} onShowOnMap={handleShowOnMap} />}
          {activePage === "stock"    && stockEnabled && <StockManager companyId={companyId} />}
          {activePage === "settings" && <CompanySettings companyId={companyId} />}
        </main>
      </div>

      {/* ── Mobile Bottom Tab Bar ───────────────────────────────────────── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 safe-area-pb">
        <div className="flex">
          {MOBILE_TABS.map(id => {
            const item   = NAV_ITEMS.find(n => n.id === id);
            const Icon   = NAV_ICONS[id];
            const isActive = activePage === id;
            return (
              <button key={id} onClick={() => navigate(id)}
                className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-colors ${
                  isActive ? "text-green-700" : "text-gray-400 active:bg-gray-50"
                }`}>
                <Icon className="w-5 h-5" />
                <span className="text-[9px] font-medium leading-none">{t(item.labelKey)}</span>
                {isActive && <span className="w-1 h-1 rounded-full bg-green-600 mt-0.5" />}
              </button>
            );
          })}

          {/* More button */}
          <button onClick={() => setMoreMenuOpen(o => !o)}
            className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-colors ${
              !MOBILE_TABS.includes(activePage) ? "text-green-700" : "text-gray-400 active:bg-gray-50"
            }`}>
            {!MOBILE_TABS.includes(activePage) && ActiveIcon
              ? <ActiveIcon className="w-5 h-5" />
              : <MoreHorizontal className="w-5 h-5" />}
            <span className="text-[9px] font-medium leading-none">More</span>
            {!MOBILE_TABS.includes(activePage) && <span className="w-1 h-1 rounded-full bg-green-600 mt-0.5" />}
          </button>
        </div>
      </nav>

      {/* ── More Menu (slide up panel) ──────────────────────────────────── */}
      {moreMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setMoreMenuOpen(false)} />
          <div className="fixed bottom-[60px] left-0 right-0 z-50 lg:hidden bg-white rounded-t-2xl shadow-2xl">
            <div className="pt-3 pb-1 flex justify-center">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="px-4 pb-5">
              <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-3">All Pages</p>
              <div className="grid grid-cols-4 gap-2">
                {NAV_ITEMS.filter(n => !MOBILE_TABS.includes(n.id)).map(item => {
                  const Icon = NAV_ICONS[item.id];
                  return (
                    <button key={item.id} onClick={() => navigate(item.id)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition ${
                        activePage === item.id
                          ? "bg-green-50 border-green-200 text-green-700"
                          : "bg-gray-50 border-gray-100 text-gray-600 active:bg-gray-100"
                      }`}>
                      <Icon className="w-6 h-6" />
                      <span className="text-[10px] font-medium text-center leading-tight">{t(item.labelKey)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SidebarItem({ Icon, label, active, onClick, badge }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
        active ? "bg-green-50 text-green-700" : "text-gray-600 hover:bg-gray-50"
      }`}>
      <Icon className="w-4 h-4 shrink-0" />
      <span className="flex-1 text-left">{label}</span>
      {badge && (
        <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
          {badge}
        </span>
      )}
    </button>
  );
}

export default AdminDashboard;
